import { Box, Stack, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { DialogWrapper } from '../../../../components/presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { useDirtyBaseline } from '../../../../hooks/use-dirty-baseline';
import { notifyError } from '../../../../shared/utils/notify-error';

export interface ISetupInstructionsRecipient {
  recipientEmail: string;
  recipientName?: string;
  note?: string;
}

/** The note limit the backend enforces for both connectors (lib/setup-instructions). */
export const SETUP_INSTRUCTIONS_NOTE_MAX = 500;

/**
 * Email setup instructions to an administrator who is usually not a user of the platform: the AWS
 * admin who creates the read-only role, or the Microsoft administrator who grants Business Central
 * access. Their reply comes back to the person sending this. Each connector supplies its own
 * wording and send call; the form, validation and resend prefill are shared.
 */
export function SetupInstructionsDialog<T>({
  open,
  onClose,
  onSent,
  send,
  adminLabel,
  description,
  initialEmail = '',
  initialName = '',
  noteMax = SETUP_INSTRUCTIONS_NOTE_MAX,
}: {
  open: boolean,
  onClose: () => void,
  onSent: (result: T) => void,
  send: (recipient: ISetupInstructionsRecipient) => Promise<T>,
  /** Who receives them, as the copy names them: "AWS admin", "Microsoft administrator". */
  adminLabel: string,
  description: ReactNode,
  /** Prefill for Resend: the last recipient. */
  initialEmail?: string,
  initialName?: string,
  noteMax?: number,
}) {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = Yup.object().shape({
    recipientEmail: Yup.string().trim().email('Enter a valid email address').required(`Enter your ${adminLabel}'s email address`),
    recipientName: Yup.string().trim().max(100, 'Keep the name to 100 characters or fewer'),
    note: Yup.string().max(noteMax, `Keep the message to ${noteMax} characters or fewer`),
  });

  const formik = useFormik({
    initialValues: { recipientEmail: initialEmail, recipientName: initialName, note: '' },
    validationSchema: schema,
    onSubmit: values => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      const recipientEmail = values.recipientEmail.trim().toLowerCase();
      send({
        recipientEmail,
        recipientName: values.recipientName.trim() || undefined,
        note: values.note.trim() || undefined,
      })
        .then(result => {
          userNotificationsContext.openSnackbar({ open: true, message: `Instructions sent to ${recipientEmail}`, backgroundColor: 'primary.dark' });
          onSent(result);
          onClose();
        })
        .catch(err => notifyError(userNotificationsContext.openSnackbar, err, 'sending the instructions'))
        .finally(() => setIsSubmitting(false));
    },
  });
  const { isDirty, rebaseline } = useDirtyBaseline(formik.values);

  useEffect(() => {
    if (!open) return;
    formik.resetForm({ values: { recipientEmail: initialEmail, recipientName: initialName, note: '' } });
    // The seed rewrites every field, so the baseline matches what the form now shows.
    rebaseline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEmail, initialName]);

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={`Email instructions to your ${adminLabel}`}
      cancelHandler={onClose}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      maxWidth='sm'
      submitButtonText='Send instructions'
      submitButtonLoadingText='Sending instructions'
    >
      <Box component='form' onSubmit={formik.handleSubmit}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {description}
          </Typography>
          <TextField
            id='recipientEmail' name='recipientEmail' label={`${adminLabel.charAt(0).toUpperCase()}${adminLabel.slice(1)}'s email`} type='email' fullWidth
            value={formik.values.recipientEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.recipientEmail && Boolean(formik.errors.recipientEmail)}
            helperText={formik.touched.recipientEmail && formik.errors.recipientEmail}
          />
          <TextField
            id='recipientName' name='recipientName' label='Their name (optional)' fullWidth
            value={formik.values.recipientName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.recipientName && Boolean(formik.errors.recipientName)}
            helperText={formik.touched.recipientName && formik.errors.recipientName}
          />
          <TextField
            id='note' name='note' label='Message (optional)' fullWidth multiline minRows={3}
            value={formik.values.note}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.note)}
            helperText={formik.errors.note || `${formik.values.note.length}/${noteMax}`}
          />
        </Stack>
      </Box>
    </DialogWrapper>
  );
}
